import { html, nothing, TemplateResult, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import { BaseComponent } from '../base-component/base-component';
import store from '../../store/store';
import { ActionStatus } from '../../common/constants/action-status';
import { loading } from '../../common/constants/api-constants';
import { StoreState } from '../../store/store.interface';
import { AccessCodeDetails, OfferDetails } from './interfaces/access-code.interface';
import { getAccessCodeData, getAccessCodeStatus } from './store/access-code.selector';
import { selectUser } from '../user-profile/user-profile.selectors';
import { redeemAccessCode } from './thunks/access-code.thunk';
import { getLoginURLWithRedirectUrlToCurrentPage } from '../../common/utils/login-utils';
import emit from '../../common/events';
import { fetchUserProfile } from '../user-profile/user-profile.thunks';
import environment from '../../common/environment';

@customElement('mhecomm-access-code-lookup-results')
export class AccessCodeLookupResults extends connect(store)(BaseComponent) {

  static styles = [css`
    .products-heading-content {
      min-width: 0;
    }

    .offers {
      max-height: 32rem;
      overflow-y: auto;
      overflow-x: hidden;
    }
  `];

  @property({ attribute: 'success-page-url' })
  successPageUrl = '';

  @state()
  private accessCodeStatus: ActionStatus = ActionStatus.idle;

  @state()
  private accessCodeData: AccessCodeDetails | undefined;

  @state()
  private product = '';

  @state()
  private redeemEligible = false;

  @state()
  private redeemStatus: 'idle' | 'pending' | 'success' | 'error' = 'idle';

  @state()
  private redeemError = '';

  @state()
  private redeemErrorCode = '';

  private previousRedeemStatus: 'idle' | 'pending' | 'success' | 'error' = 'idle';

  private previousAccessCodeStatus: ActionStatus = ActionStatus.idle;

  private previousAccessCodeData: AccessCodeDetails | undefined;

  @state()
  private isSignedIn = false;

  connectedCallback() {
    super.connectedCallback();
    store.dispatch(fetchUserProfile());
  }

  stateChanged(state: StoreState) {
    this.accessCodeStatus = getAccessCodeStatus(state);
    this.accessCodeData = getAccessCodeData(state);
    this.product = state.accessCodeState.product || '';
    this.redeemEligible = state.accessCodeState.redeemEligible || false;
    this.redeemStatus = state.accessCodeState.redeemStatus || 'idle';
    this.redeemError = state.accessCodeState.error || '';
    this.redeemErrorCode = state.accessCodeState.redeemErrorCode || '';
    this.isSignedIn = !!state.userProfileState && !!selectUser(state)?.givenName;
    if (this.redeemStatus !== this.previousRedeemStatus) {
      if (this.redeemStatus === 'success') {
        this.emitRedeemEvent('redeem-succeeded');
      } else if (this.redeemStatus === 'error' && this.isMappedRedeemError()) {
        this.emitRedeemEvent('redeem-failed');
      }
      this.previousRedeemStatus = this.redeemStatus;
    }
    if (this.redeemStatus === 'success' && this.successPageUrl) {
      window.location.href = this.successPageUrl;
    }
    // Only emit once per lookup result; redeem-status re-renders shouldn't re-fire it.
    const isResolved = this.accessCodeStatus !== ActionStatus.idle && this.accessCodeStatus !== ActionStatus.loading;
    if (isResolved && (this.accessCodeStatus !== this.previousAccessCodeStatus || this.accessCodeData !== this.previousAccessCodeData)) {
      this.emitLookupResultEvent();
    }
    this.previousAccessCodeStatus = this.accessCodeStatus;
    this.previousAccessCodeData = this.accessCodeData;
  }

  private emitLookupResultEvent() {
    const isRedeemable =
      this.accessCodeStatus === ActionStatus.succeeded && this.accessCodeData?.status === 'REDEEMABLE';
    const rawCode = this.accessCodeData?.accessCode
        || new URLSearchParams(window.location.search).get('code')
        || '';
    const displayCode = rawCode ? this.formatAccessCode(rawCode) : '';
    const statusLabel = isRedeemable
      ? 'Redeemable'
      : this.accessCodeData?.status === 'USED'
        ? 'Used Code'
        : this.accessCodeData?.status === 'EXPIRED'
          ? 'Expired'
        : this.accessCodeData?.status || 'Invalid';
    emit({
        element: this,
        name:'access-code-lookup-result',
        detail: { 
          accessCode: displayCode,
          accessCodeStatus: statusLabel,
          productType: this.product || undefined,
          redeemableProducts: this.accessCodeData?.offers?.length || 0
        }
      }
    );
  }

  renderComp(): TemplateResult | typeof nothing {
    if (this.accessCodeStatus === ActionStatus.idle) {
      return nothing;
    }

    if (this.accessCodeStatus === ActionStatus.loading) {
      return html`
        <div class="mt-3" role="status" aria-live="polite">
          ${loading({ columns: 1, m: 'mt-1', width: '100%', height: '1.25rem' })}
          ${loading({ columns: 1, m: 'mt-2', width: '100%', height: '5rem' })}
        </div>
      `;
    }

    const isRedeemable =
      this.accessCodeStatus === ActionStatus.succeeded && this.accessCodeData?.status === 'REDEEMABLE';
    const rawCode = this.accessCodeData?.accessCode
        || new URLSearchParams(window.location.search).get('code')
        || '';
    const displayCode = rawCode ? this.formatAccessCode(rawCode) : '';
    const statusLabel = isRedeemable
      ? 'Redeemable'
      : this.accessCodeData?.status === 'USED'
        ? 'Used Code'
        : this.accessCodeData?.status === 'EXPIRED'
          ? 'Expired'
        : this.accessCodeData?.status || 'Invalid';
    return html`
      <div class="alert alert-${isRedeemable ? 'info' : 'danger'} mb-4" role="status" aria-live="polite">
        <div><strong>Status</strong> ${statusLabel}</div>
        ${this.product && this.accessCodeData?.status !== 'INVALID'
          ? html`<div><strong>Product</strong> ${this.product}</div>`
          : nothing}
        <div><strong>Access Code</strong> ${displayCode}</div>
      </div>
      ${ 
        isRedeemable ? html `
          <div class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-start justify-content-between gap-3 px-0 mb-4">
              <div class="products-heading-content">
                <h2 class="fs-4 mb-1">Products associated with this access code${(this.accessCodeData?.offers || []).length > 1 ? ` (${this.accessCodeData?.offers.length})` : ''}</h2>
                ${!this.isSignedIn && this.redeemEligible ? html`<p class="mb-0"><strong>Note:</strong> Sign in is required to redeem an access code.</p>` : nothing}
              </div>
              ${this.redeemEligible ? html`
                <button class="btn btn-primary flex-shrink-0" ?disabled=${this.redeemStatus === 'pending'} @click=${this.redeem.bind(this)}>
                  ${this.isSignedIn ? 'Redeem' : 'Login to Redeem'}
                </button>
              ` : nothing}
          </div>
          <div class="offers card">
              ${(this.accessCodeData?.offers || []).map((offer, index) => this.renderOffer(offer, index > 0))}
          </div>
          ${this.redeemEligible ? html`
            ${this.redeemError ? html`<p class="alert alert-danger mt-3" role="alert">${this.getRedeemErrorMessage()}</p>` : nothing}
          ` : nothing}
        `: nothing
      }
      
    `;
  }

  private formatAccessCode(code: string): string {
    // Strip any existing hyphens, then insert one after every 4th character: XXXX-XXXX-XXXX-XXXX-XXXX
    const stripped = code.replace(/-/g, '');
    return stripped.replace(/(.{4})(?=.)/g, '$1-');
  }

  private redeem() {
    if (!this.isSignedIn) {
      this.emitRedeemEvent('login-to-redeem-clicked');
      const loginUrl = new URL(getLoginURLWithRedirectUrlToCurrentPage());
      loginUrl.searchParams.set('flow', 'access_code');
      window.location.href = loginUrl.toString();
      return;
    }

    this.emitRedeemEvent('redeem-clicked');
    const code = this.accessCodeData?.accessCode || '';
    if (this.product === 'eBook') {
      window.location.href = `${environment().myBookShelfUrl}/bookshelf/redeemcode`;
      return;
    }

    this.emitRedeemEvent('redeem-submitted');
    store.dispatch(redeemAccessCode(code) as any);
  }

  private emitRedeemEvent(name: string) {
    emit({
      element: this,
      name,
      detail: this.getRedeemEventDetail()
    });
  }

  private getRedeemEventDetail() {
    const status = this.accessCodeData?.status?.toLowerCase() as
      'redeemable' | 'used' | 'expired' | 'invalid' | undefined;
    return {
      status,
      product: this.product || undefined,
      productsCount: this.accessCodeData?.offers?.length || 0
    };
  }

  private isMappedRedeemError(): boolean {
    const errorCode = this.redeemErrorCode.toUpperCase();
    return [
      'USED',
      'ACCESS_CODE_USED',
      'INVALID',
      'ACCESS_CODE_INVALID',
      'NOT_FOUND',
      'INELIGIBLE_ACCOUNT',
      'ACCOUNT_TYPE_INELIGIBLE'
    ].includes(errorCode);
  }

  private getRedeemErrorMessage(): string {
    const errorCode = this.redeemErrorCode.toUpperCase();
    if (['USED', 'ACCESS_CODE_USED'].includes(errorCode)) {
      return 'This access code has already been used.';
    }
    if (['INVALID', 'ACCESS_CODE_INVALID', 'NOT_FOUND'].includes(errorCode)) {
      return 'This access code is no longer valid.';
    }
    if (['INELIGIBLE_ACCOUNT', 'ACCOUNT_TYPE_INELIGIBLE'].includes(errorCode)) {
      return 'This account is not eligible to redeem this access code.';
    }
    return this.redeemError;
  }

  private renderOffer(offer: OfferDetails, addDivider: boolean): TemplateResult {
    const isbn = offer.isbn || this.accessCodeData?.cardIsbn || 'N/A';
    const term = offer.term || 'N/A';

    return html`
      ${addDivider ? html`<hr class="my-0 mx-4" />` : nothing}
      <div class="row">
        <div class="col-md-2 col-4 p-3 mx-md-3">
          ${offer.coverImageUrl
            ? html`<img class="d-block w-100 rounded" src=${offer.coverImageUrl} alt="" aria-hidden="true" />`
            : nothing}
        </div>
        <div class="col align-top p-3">
          <h3 class="fs-5 fw-bold">${offer.productTitle || 'Untitled Product'}</h3>
          ${offer.edition ? html`<p class="mb-1"><strong>Edition:</strong> ${offer.edition}</p>` : nothing}
          <p class="mb-1"><strong>ISBN</strong> ${isbn}</p>
          <p class="mb-0">Instant Access at the end of your purchase (access provided for ${term}).</p>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-access-code-lookup-results': AccessCodeLookupResults;
  }
}

